import { parseInitialURLAsync } from 'expo-linking';

import { CONFIG } from '../../../../launch-darkly.config';
import { type BaseFeatureFlags } from '../../../../launch-darkly.types';
import { safelyParseFeatureFlagValue } from '../safely-parse-feature-flag-value';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns feature flag overrides query params from the initial URL.
 *
 * @see {@link https://docs.expo.dev/guides/linking/}
 * @see {@link https://docs.expo.dev/versions/latest/sdk/linking/#linkingparseinitialurlasync}
 */
export async function getFeatureFlagOverridesFromInitialUrl<
  FeatureFlags extends BaseFeatureFlags,
>(): Promise<Partial<FeatureFlags> | undefined> {
  try {
    const { queryParams } = await parseInitialURLAsync();

    if (!queryParams) return;

    const queryParamsEntries = Object.entries(queryParams);
    const hasFeatureFlagOverrides = queryParamsEntries.some(checkIfFeatureFlagOverride); // prettier-ignore

    if (!hasFeatureFlagOverrides) return;

    return queryParamsEntries.reduce<Partial<FeatureFlags>>(
      addFeatureFlagOverrideEntry,
      {},
    );
  } catch {
    return undefined;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Handles filtering and formatting of feature flag override entries for reduce.
 */
function addFeatureFlagOverrideEntry<FeatureFlags extends BaseFeatureFlags>(
  filteredEntries: Partial<FeatureFlags>,
  queryParamEntry: [string, unknown],
): Partial<FeatureFlags> {
  const [key, value] = queryParamEntry;

  const hasValidValue = typeof value === 'string';
  const isFeatureFlagOverride = checkIfFeatureFlagOverride(queryParamEntry);
  const shouldIncludeQueryParam = isFeatureFlagOverride && hasValidValue;
  const keyWithoutPrefix = removeOverridePrefix(key);

  if (shouldIncludeQueryParam) {
    return {
      ...filteredEntries,
      [keyWithoutPrefix]: safelyParseFeatureFlagValue(value),
    };
  }

  return filteredEntries;
}

/**
 * Checks if a query param key is a feature flag override.
 */
function checkIfFeatureFlagOverride(
  queryParamEntry: [string, unknown],
): boolean {
  const [key] = queryParamEntry;

  return key.startsWith(CONFIG.FEATURE_FLAG_OVERRIDE_QUERY_PARAM_PREFIX);
}

function removeOverridePrefix(key: string): string {
  return key.replace(CONFIG.FEATURE_FLAG_OVERRIDE_QUERY_PARAM_PREFIX, '');
}
